import { AuthKitProvider, useAuth as useWorkosAuth } from "@workos-inc/authkit-nextjs/components"
import type { PropsWithChildren, ReactElement } from "react"
import { Fragment, createElement } from "react"
import {
  buildWorkosHostedAuthPath,
  createWorkosAuthClient,
  mapWorkosAuthToAuthOrganizationState,
  mapWorkosAuthToAuthUserState,
} from "./workos-client"

export { useAuth } from "./hooks/use-auth"

export const authProvider: string = "workos"

const throwUnsupportedClientSurface = () => {
  throw new Error("This auth surface is not available with WorkOS hosted auth")
}

export function AuthProvider(props: PropsWithChildren<{ localization?: unknown }>): ReactElement {
  return createElement(AuthKitProvider, null, props.children)
}

export function SignedIn(props: PropsWithChildren<object>) {
  const auth = useWorkosAuth()

  if (auth.loading || !auth.user) {
    return null
  }

  return createElement(Fragment, null, props.children)
}

export function SignedOut(props: PropsWithChildren<object>) {
  const auth = useWorkosAuth()

  if (auth.loading || auth.user) {
    return null
  }

  return createElement(Fragment, null, props.children)
}

export function UserButton(_props?: Record<string, unknown>) {
  const auth = useWorkosAuth()
  const authClient = createWorkosAuthClient(auth)
  const userState = mapWorkosAuthToAuthUserState(auth)
  const label = userState.user?.firstName?.[0] ?? userState.user?.username?.[0] ?? "?"

  return createElement(
    "button",
    {
      "aria-label": "User menu",
      className:
        "inline-flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 bg-white text-sm font-medium text-neutral-700",
      onClick: () => {
        void authClient.signOut()
      },
      type: "button",
    },
    label
  )
}

export function SignInButton(props: PropsWithChildren<Record<string, unknown>>) {
  createElement(
    "a",
    {
      href: "/sign-in",
      ...props,
    },
    props.children
  )
}

export function SignUpButton(props: PropsWithChildren<Record<string, unknown>>) {
  createElement(
    "a",
    {
      href: "/sign-up",
      ...props,
    },
    props.children
  )
}

export const useSignIn: any = () => ({
  isLoaded: true,
  setActive: async (_options?: Record<string, unknown>) => {},
  signIn: {
    create: async (_options?: Record<string, unknown>) => {
      const location = (globalThis as { location?: { assign(url: string): void } }).location

      if (!location) {
        throw new Error("WorkOS sign-in redirects are only available in the browser")
      }

      location.assign(buildWorkosHostedAuthPath("sign-in"))

      return new Promise<{ status: string; createdSessionId?: string }>(() => {})
    },
    authenticateWithRedirect: async (
      options: { redirectUrlComplete?: string | null; strategy?: string; redirectUrl?: string } = {}
    ) => {
      const location = (globalThis as { location?: { assign(url: string): void } }).location

      if (!location) {
        throw new Error("WorkOS sign-in redirects are only available in the browser")
      }

      const returnPath = options.redirectUrlComplete?.startsWith("/") ? options.redirectUrlComplete : null

      location.assign(buildWorkosHostedAuthPath("sign-in", returnPath))

      return new Promise<{ status: string; createdSessionId?: string }>(() => {})
    },
  },
})

export const useSignUp: any = () => ({
  isLoaded: true,
  setActive: async (_options?: Record<string, unknown>) => {},
  signUp: {
    create: async (_options?: Record<string, unknown>) => {
      const location = (globalThis as { location?: { assign(url: string): void } }).location

      if (!location) {
        throw new Error("WorkOS sign-up redirects are only available in the browser")
      }

      location.assign(buildWorkosHostedAuthPath("sign-up"))

      return new Promise<{ status: string; createdSessionId?: string }>(() => {})
    },
    update: async (_options?: Record<string, unknown>) => {
      const location = (globalThis as { location?: { assign(url: string): void } }).location

      if (!location) {
        throw new Error("WorkOS sign-up redirects are only available in the browser")
      }

      location.assign(buildWorkosHostedAuthPath("sign-up"))

      return new Promise<{ status: string; createdSessionId?: string }>(() => {})
    },
    prepareEmailAddressVerification: async (_options?: Record<string, unknown>) => {
      const location = (globalThis as { location?: { assign(url: string): void } }).location

      if (!location) {
        throw new Error("WorkOS sign-up redirects are only available in the browser")
      }

      location.assign(buildWorkosHostedAuthPath("sign-up"))

      return new Promise<{ status: string; createdSessionId?: string }>(() => {})
    },
    attemptEmailAddressVerification: async (_options?: Record<string, unknown>) => {
      const location = (globalThis as { location?: { assign(url: string): void } }).location

      if (!location) {
        throw new Error("WorkOS sign-up redirects are only available in the browser")
      }

      location.assign(buildWorkosHostedAuthPath("sign-up"))

      return new Promise<{ status: string; createdSessionId?: string }>(() => {})
    },
    authenticateWithRedirect: async (
      options: { redirectUrlComplete?: string | null; strategy?: string; redirectUrl?: string } = {}
    ) => {
      const location = (globalThis as { location?: { assign(url: string): void } }).location

      if (!location) {
        throw new Error("WorkOS sign-up redirects are only available in the browser")
      }

      const returnPath = options.redirectUrlComplete?.startsWith("/") ? options.redirectUrlComplete : null

      location.assign(buildWorkosHostedAuthPath("sign-up", returnPath))

      return new Promise<{ status: string; createdSessionId?: string }>(() => {})
    },
  },
})

export const AuthenticateWithRedirectCallback = () => null
export const CreateOrganization = () => throwUnsupportedClientSurface()
export const OrganizationSwitcher = () => null
export const SignIn = () => throwUnsupportedClientSurface()
export const SignUp = () => throwUnsupportedClientSurface()
export const useAuthClient: any = () => createWorkosAuthClient(useWorkosAuth())
export const useOrganization: any = () => mapWorkosAuthToAuthOrganizationState(useWorkosAuth())
export const useUser: any = () => mapWorkosAuthToAuthUserState(useWorkosAuth())
export const useAuthOrganization: any = useOrganization
export const useAuthUser: any = useUser

type AuthOrganization = NonNullable<ReturnType<typeof useOrganization>["organization"]>

export type AuthOrganizationMembership = Awaited<ReturnType<AuthOrganization["getMemberships"]>>["data"][number]

export type AuthOrganizationInvitation = Awaited<ReturnType<AuthOrganization["getInvitations"]>>["data"][number]

export const authJaJP = undefined
